"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  convertItalianMark,
  DEFAULT_CUSTOM_MINS,
  DEGREE_PASS_MARK,
  type DegreeMark,
  degreeMark,
  degreeToExamScale,
  ITALIAN_SCALES,
  type ItalianScale,
  type ItalianSummary,
  italianBands,
  summariseItalian,
  validCustomMins,
} from "@/lib/tool-kit/intl-grades/italian-grades";

/** English strings. A translated content file overrides these through `ui`. */
export const ITALIAN_GRADE_CONVERTER_STRINGS = {
  scaleLabel: "US conversion table for exam marks",
  scales: {
    montana: "University of Montana — 27–30 A, 21–26 B, 18–20 C",
    uw: "University of Washington Graduate School — 27–30 A, 24–26 B, 21–23 C, 18–20 D",
    custom: "Custom cutoffs — type your own",
  },
  scaleHint:
    "There is no official Italy-to-US table. These are two published ones that disagree below 24. Use the one your receiving university states.",
  customLegend: "Lowest mark for each letter (18–30)",
  customA: "A from",
  customB: "B from",
  customC: "C from",
  customD: "D from",
  customInvalid:
    "Cutoffs must be whole numbers from 18 to 30, falling from A to D.",
  examsHeading: "Your exam marks (18–30)",
  markLabel: "Mark for exam {n}",
  creditsLabel: "Credits (CFU) for exam {n}",
  lodeLabel: "30 e lode (exam {n})",
  addExam: "Add an exam",
  removeExam: "Remove",
  removeExamAria: "Remove exam {n}",
  degreeHeading: "Degree mark (voto di laurea)",
  bonusLabel: "Commission points added to the base mark",
  bonusHint:
    "Set by your degree regulation: thesis, speed bonus and so on. Bologna's electronic engineering degree allows up to 4 for the final exam, for example.",
  lodeGranted: "The commission awards the lode (needs a final mark of 110)",
  results: "Result",
  average: "Weighted average /30",
  gpa: "GPA (4.0 scale)",
  share: "Share of the maximum mark",
  baseDegree: "Base degree mark /110",
  finalDegree: "Final degree mark",
  degreeOnExamScale: "Final mark on the 30 scale",
  lodi: "Exams with lode",
  withLode: "110 e lode",
  cappedNote: "The total passed 110, so the final mark is capped at 110.",
  belowPass: "Below {pass}: a degree mark under {pass} is not a pass.",
  tableHeading: "Exam by exam",
  colExam: "Exam",
  colMark: "Mark",
  colLetter: "Letter",
  colCredits: "Credits",
  examName: "Exam {n}",
  lodeSuffix: "{mark} e lode",
  skipped: "Skipped (not a whole mark from 18 to 30, or no credits): {rows}",
  empty: "Enter at least one exam mark from 18 to 30 with credits above zero.",
  caveat:
    "The US GPA comes from your exam marks, not from the degree mark. A university that reads the /110 mark will usually use its own table.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "Italian average {average}/30 = GPA {gpa}; base degree mark {base}/110",
} as const;

type Strings = typeof ITALIAN_GRADE_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface Row {
  id: number;
  mark: string;
  credits: string;
  lode: boolean;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, mark: "28", credits: "9", lode: false },
  { id: 2, mark: "30", credits: "6", lode: true },
  { id: 3, mark: "25", credits: "12", lode: false },
  { id: 4, mark: "27", credits: "6", lode: false },
  { id: 5, mark: "22", credits: "9", lode: false },
];

function ExamRow({
  row,
  index,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <TextField
          id={`igc-mark-${row.id}`}
          label={t(strings.markLabel, { n })}
          numeric
          onChange={(mark) => onChange({ ...row, mark })}
          value={row.mark}
        />
        <TextField
          id={`igc-credits-${row.id}`}
          label={t(strings.creditsLabel, { n })}
          numeric
          onChange={(credits) => onChange({ ...row, credits })}
          value={row.credits}
        />
        <button
          aria-label={t(strings.removeExamAria, { n })}
          className={BUTTON_SECONDARY}
          onClick={onRemove}
          type="button"
        >
          {strings.removeExam}
        </button>
      </div>
      <Checkbox
        checked={row.lode}
        label={t(strings.lodeLabel, { n })}
        onChange={(lode) => onChange({ ...row, lode })}
      />
    </div>
  );
}

function CustomFields({
  raw,
  customOk,
  onChange,
  strings,
}: {
  raw: Record<"a" | "b" | "c" | "d", string>;
  customOk: boolean;
  onChange: (key: "a" | "b" | "c" | "d", value: string) => void;
  strings: Strings;
}) {
  return (
    <fieldset className="space-y-3">
      <legend className="font-medium text-foreground text-sm">
        {strings.customLegend}
      </legend>
      <div className="grid gap-3 sm:grid-cols-4">
        {(
          [
            ["a", strings.customA],
            ["b", strings.customB],
            ["c", strings.customC],
            ["d", strings.customD],
          ] as const
        ).map(([key, label]) => (
          <TextField
            id={`igc-custom-${key}`}
            key={key}
            label={label}
            numeric
            onChange={(value) => onChange(key, value)}
            value={raw[key]}
          />
        ))}
      </div>
      {customOk ? null : <Notice>{strings.customInvalid}</Notice>}
    </fieldset>
  );
}

function ResultsSection({
  summary,
  degree,
  customOk,
  skipped,
  tableRows,
  strings,
  fmt,
}: {
  summary: ItalianSummary | null;
  degree: DegreeMark | null;
  customOk: boolean;
  skipped: string[];
  tableRows: string[][];
  strings: Strings;
  fmt: Format;
}) {
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {summary === null || degree === null ? (
        <Notice>{customOk ? strings.empty : strings.customInvalid}</Notice>
      ) : (
        <>
          <StatGrid>
            <StatCard
              label={strings.average}
              value={fmt(summary.average, { maximumFractionDigits: 2 })}
            />
            <StatCard
              emphasis
              label={strings.gpa}
              value={fmt(summary.gpa, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            />
            <StatCard
              label={strings.share}
              value={fmt(summary.share, {
                style: "percent",
                maximumFractionDigits: 1,
              })}
            />
            <StatCard
              label={strings.baseDegree}
              value={fmt(summary.baseDegreeMark, {
                maximumFractionDigits: 2,
              })}
            />
            <StatCard
              emphasis
              label={strings.finalDegree}
              value={
                degree.lode
                  ? strings.withLode
                  : fmt(degree.final, { maximumFractionDigits: 0 })
              }
            />
            <StatCard
              label={strings.degreeOnExamScale}
              value={fmt(degreeToExamScale(degree.final), {
                maximumFractionDigits: 2,
              })}
            />
          </StatGrid>
          <p className="text-muted-foreground text-sm">
            {strings.lodi}: {fmt(summary.lodi)}
          </p>
          {degree.capped ? <Notice>{strings.cappedNote}</Notice> : null}
          {degree.passed ? null : (
            <Notice>
              {t(strings.belowPass, { pass: fmt(DEGREE_PASS_MARK) })}
            </Notice>
          )}
          {skipped.length > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.skipped, { rows: skipped.join(", ") })}
            </p>
          ) : null}
          <DataTable
            caption={strings.tableHeading}
            head={[
              strings.colExam,
              strings.colMark,
              strings.colLetter,
              strings.colCredits,
            ]}
            rows={tableRows}
          />
          <p className="text-muted-foreground text-sm">{strings.caveat}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={t(strings.summary, {
              average: fmt(summary.average, { maximumFractionDigits: 2 }),
              gpa: fmt(summary.gpa, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              base: fmt(summary.baseDegreeMark, {
                maximumFractionDigits: 2,
              }),
            })}
          />
        </>
      )}
    </section>
  );
}

export default function ItalianGradeConverterWidget() {
  const strings = useToolUi(ITALIAN_GRADE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [scale, setScale] = useState<ItalianScale>("uw");
  const [customRaw, setCustomRaw] = useState({
    a: String(DEFAULT_CUSTOM_MINS.a),
    b: String(DEFAULT_CUSTOM_MINS.b),
    c: String(DEFAULT_CUSTOM_MINS.c),
    d: String(DEFAULT_CUSTOM_MINS.d),
  });
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(6);
  const [bonusRaw, setBonusRaw] = useState("0");
  const [lodeGranted, setLodeGranted] = useState(false);

  const mins = {
    a: parseNumberInput(customRaw.a) ?? Number.NaN,
    b: parseNumberInput(customRaw.b) ?? Number.NaN,
    c: parseNumberInput(customRaw.c) ?? Number.NaN,
    d: parseNumberInput(customRaw.d) ?? Number.NaN,
  };
  const customOk = scale !== "custom" || validCustomMins(mins);
  const bands = italianBands(scale, customOk ? mins : DEFAULT_CUSTOM_MINS);

  const parsed = rows.map((row) => ({
    row,
    mark: parseNumberInput(row.mark),
    credits: parseNumberInput(row.credits),
  }));
  const exams = parsed.flatMap(({ row, mark, credits }) =>
    mark === null || credits === null ? [] : [{ mark, credits, lode: row.lode }]
  );
  const summary = customOk ? summariseItalian(exams, bands) : null;
  const skipped = parsed.flatMap(({ mark, credits }, index) =>
    mark !== null &&
    credits !== null &&
    credits > 0 &&
    convertItalianMark(mark, bands)
      ? []
      : [fmt(index + 1)]
  );
  const bonus = parseNumberInput(bonusRaw) ?? 0;
  const degree = summary
    ? degreeMark(summary.baseDegreeMark, bonus, lodeGranted)
    : null;

  const tableRows = parsed.flatMap(({ row, mark, credits }, index) => {
    const band = mark === null ? null : convertItalianMark(mark, bands);
    if (mark === null || credits === null || band === null || credits <= 0) {
      return [];
    }
    return [
      [
        t(strings.examName, { n: fmt(index + 1) }),
        row.lode && mark === 30
          ? t(strings.lodeSuffix, { mark: fmt(mark) })
          : fmt(mark),
        band.letter,
        fmt(credits, { maximumFractionDigits: 2 }),
      ],
    ];
  });

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.scaleHint}
          id="igc-scale"
          label={strings.scaleLabel}
          onChange={setScale}
          options={ITALIAN_SCALES.map((value) => ({
            value,
            label: strings.scales[value],
          }))}
          value={scale}
        />
        {scale === "custom" ? (
          <CustomFields
            customOk={customOk}
            onChange={(key, value) =>
              setCustomRaw((current) => ({ ...current, [key]: value }))
            }
            raw={customRaw}
            strings={strings}
          />
        ) : null}
        <h3 className={WIDGET_H3}>{strings.examsHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <ExamRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, mark: "", credits: "6", lode: false },
            ]);
            setNextId((value) => value + 1);
          }}
          type="button"
        >
          {strings.addExam}
        </button>
        <h3 className={WIDGET_H3}>{strings.degreeHeading}</h3>
        <TextField
          hint={strings.bonusHint}
          id="igc-bonus"
          label={strings.bonusLabel}
          numeric
          onChange={setBonusRaw}
          value={bonusRaw}
        />
        <Checkbox
          checked={lodeGranted}
          label={strings.lodeGranted}
          onChange={setLodeGranted}
        />
      </div>

      <ResultsSection
        customOk={customOk}
        degree={degree}
        fmt={fmt}
        skipped={skipped}
        strings={strings}
        summary={summary}
        tableRows={tableRows}
      />
    </div>
  );
}
